import { useEffect, useState, useRef } from 'react';
import { useTranslation } from '@/i18n';
import { useScrollReveal } from './useScrollReveal';

const numberFormatter = new Intl.NumberFormat('en-US');

function formatNumber(n: number): string {
  return numberFormatter.format(n);
}

function useCountUp(target: number, duration = 2000) {
  const [value, setValue] = useState(0);
  const started = useRef(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          observer.unobserve(el);

          if (prefersReduced) {
            setValue(target);
            return;
          }

          const start = performance.now();
          const step = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(Math.round(eased * target));
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return { value, ref };
}

interface StatProps {
  target: number;
  suffix: string;
  label: string;
}

function Stat({ target, suffix, label }: StatProps) {
  const { value, ref } = useCountUp(target);
  return (
    <div ref={ref} className="text-center">
      <div className="text-3xl md:text-4xl font-bold text-gray-900">
        {formatNumber(value)}{suffix}
      </div>
      <div className="text-sm text-gray-500 mt-1">{label}</div>
    </div>
  );
}

export function SocialProofBar() {
  const { t } = useTranslation();
  const sectionRef = useScrollReveal();

  return (
    <section
      ref={sectionRef}
      className="landing-reveal border-y border-gray-100 bg-gray-50/50 py-10"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
          <Stat target={500} suffix="+" label={t('landing.socialProof.merchants')} />
          <Stat target={10000} suffix="+" label={t('landing.socialProof.shared')} />
          <div className="col-span-2 md:col-span-1">
            <Stat target={250} suffix="K+" label={t('landing.socialProof.earned')} />
          </div>
        </div>
      </div>
    </section>
  );
}
